import { useMemo, useState } from 'react'

import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'

import { useBidLimits } from '~/hooks/bidding/useBidLimits'
import { useSpotFeeYenPerKwh } from '~/pages/SpotBidding/hooks/useSpotFeeYenPerKwh'
import {
  type SpotFeeAutoConsider,
  isSpotFeeAutoConsiderPending,
  resolveSpotFeeAutoConsider,
} from '~/pages/SpotBidding/models/spotFeeAutoConsider'

export type SpotFeeAutoConsiderState = {
  /** 価格欄の解釈。 開いている間は、 ON か OFF かが分かった時点の扱いのまま変わらない。 */
  feeAutoConsider: SpotFeeAutoConsider
  /**
   * 入札制限 (ON/OFF の設定を含む) を、 いま読めていない (取得中・取得失敗)。 解釈を固定した後の
   * 再取得の失敗でも true になり、 保存だけを止める (未調整のまま発注しない)。
   */
  isSettingsPending: boolean
}

/**
 * 個別作成・一括作成・ブロック入札の作成と、 下書きの編集のモーダルが、 手数料自動考慮 (スポット) を
 * どう扱うか (`spot-bidding.md` AC-BSP-004b)。 ブロック入札も通常入札と同じ「手数料自動考慮
 * (スポット)」の設定と手数料単価を使う。
 *
 * ON か OFF かが分かった時点 (入札制限の取得が終わった時点) の扱いを、 `isOpen` の間は固定する。
 * ON でも手数料単価を使えない間は OFF として扱う (`'fallbackOff'`) が、 その後に単価を取得できても
 * ON へ戻さない。 入力済みの入札価格が希望価格に読み替わって変わるのを防ぐためで、 次にモーダルを
 * 開いた時から ON の扱いに戻る (`conventions.md`「設定・マスタを変えたときの進行中のものの扱い」
 * 原則 2 の例外)。 `isOpen` は呼び出し側が必ず渡す。 開くたびに mount し直されるモーダル
 * (key 付き・条件付き描画) は、 mount している間は開いているので `true` を渡す。
 *
 * 固定するのは価格の解釈だけで、 保存の可否は固定しない。 入札制限を読めない間は
 * `isSettingsPending` が立ち、 固定した後の再取得の失敗でも保存を止める。
 */
export function useSpotFeeAutoConsider(
  isOpen: boolean,
): SpotFeeAutoConsiderState {
  const {
    isLoading: bidLimitsLoading,
    isError: bidLimitsError,
    feeAutoConsiderEnabled,
  } = useBidLimits(BidSegment.SPOT_NORMAL)
  const { feeYenPerKwh } = useSpotFeeYenPerKwh()
  // 値が変わらない間は同じ参照を返し、 呼び出し側の useMemo / effect の依存に入れても
  // 余計に再計算しない。
  const live = useMemo(
    () =>
      resolveSpotFeeAutoConsider({
        bidLimitsLoading,
        bidLimitsError,
        feeAutoConsiderEnabled,
        feeYenPerKwh,
      }),
    [bidLimitsLoading, bidLimitsError, feeAutoConsiderEnabled, feeYenPerKwh],
  )
  const isSettingsPending = isSpotFeeAutoConsiderPending(live)

  const [fixed, setFixed] = useState<SpotFeeAutoConsider | undefined>(undefined)
  if (!isOpen) {
    if (fixed !== undefined) setFixed(undefined)
    return { feeAutoConsider: live, isSettingsPending }
  }
  if (fixed === undefined && !isSettingsPending) {
    setFixed(live)
  }
  return { feeAutoConsider: fixed ?? live, isSettingsPending }
}
